import PropTypes from "prop-types";
import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Paper, Typography, Checkbox } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import moment from "moment";
import CourseActivityIcon from "../../../../components/Icons/CourseActivityIcon";
import Tooltip from "../../../../components/Tooltip/Tooltip";

const useStyles = makeStyles(theme => ({
  root: {
    cursor: "pointer",
    overflow: "hidden"
  },
  imageWrapper: {
    height: 194,
    backgroundColor: "#e7e7e7"
  },
  image: {
    width: "100%",
    height: "100%",
    maxHeight: 194,
    objectFit: "cover"
  },
  selected: {
    position: "relative",
    border: `1px solid ${theme.palette.primary.main}`
  },
  overlay: {
    position: "absolute",
    width: "100%",
    height: "100%",
    zIndex: 2,
    background: theme.palette.primary.main,
    opacity: 0.08
  },
  checkbox: {
    position: "absolute",
    zIndex: 3,
    right: 12,
    bottom: 12
  },
  title: {
    maxWidth: "calc(100% - 30px)",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    overflow: "hidden"
  },
  tooltipTitle: {
    fontSize: "0.8125rem",
    fontWeight: 600
  },
  tooltipDescription: {
    fontSize: "0.75rem",
    paddingTop: theme.spacing(1)
  }
}));

const useItemInfoStyles = makeStyles(theme => ({
  label: {
    fontWeight: 600,
    marginRight: theme.spacing(1)
  },
  value: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis"
  }
}));

const RepositoryItemInfo = ({ label, value }) => {
  const classes = useItemInfoStyles();
  return (
    <Box display="flex">
      <Typography
        variant="subtitle2"
        color="textSecondary"
        className={classes.label}
      >
        {`${label}:`}
      </Typography>
      <Typography
        variant="subtitle2"
        color="textSecondary"
        className={classes.value}
        title={value}
      >
        {value}
      </Typography>
    </Box>
  );
};

const RepositoryItemCard = ({
  onSelectRepositoryItem,
  onOpenRepositoryItem,
  name,
  description,
  type,
  courses,
  creator,
  latestVersion,
  lastUpdated,
  repositoryAccessType,
  selected,
  style
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  return (
    <Paper
      style={style}
      className={clsx(classes.root, {
        [classes.selected]: selected
      })}
      onClick={ev => {
        if (ev.target.type === "checkbox") {
          ev.stopPropagation();
          return;
        }
        onOpenRepositoryItem();
      }}
    >
      {selected && <Box className={classes.overlay} />}
      <Box display="flex" flexDirection="column">
        <Box display="flex" flexDirection="column" px={3} pb={2} pt={3}>
          <Box pt={1} pb={2} display="flex" alignItems="center">
            {type && (
              <Box pr={1.5} mb="-1px" flex="0 0 30px">
                <CourseActivityIcon
                  type={type}
                  fontSize="small"
                  color="action"
                />
              </Box>
            )}
            <Tooltip
              title={
                <Box display="flex" flexDirection="column">
                  <Typography
                    color="textPrimary"
                    className={classes.tooltipTitle}
                  >
                    {name}
                  </Typography>
                  {!!description && (
                    <Typography
                      color="textPrimary"
                      className={classes.tooltipDescription}
                    >
                      {description}
                    </Typography>
                  )}
                </Box>
              }
            >
              <Typography variant="h6" className={classes.title}>
                {name}
              </Typography>
            </Tooltip>
          </Box>
          <RepositoryItemInfo
            label={t("manageRepository.courses")}
            value={courses}
          />
          {repositoryAccessType === "owned" && (
            <RepositoryItemInfo
              label={t("manageRepository.creator")}
              value={creator}
            />
          )}
          <RepositoryItemInfo
            label={t("manageRepository.latestVersion")}
            value={latestVersion}
          />
          <RepositoryItemInfo
            label={t("manageRepository.lastUpdated")}
            value={moment(lastUpdated).format("MM-DD-YYYY")}
          />
        </Box>
      </Box>
      <Box className={classes.checkbox}>
        <Checkbox
          checked={!!selected}
          color="primary"
          onChange={onSelectRepositoryItem}
        />
      </Box>
    </Paper>
  );
};

RepositoryItemCard.propTypes = {
  /** A string with the list of associated courses */
  courses: PropTypes.string,
  /**
   * The creator of the item
   */
  creator: PropTypes.string,
  /**
   * The image for the item
   */
  imageSrc: PropTypes.any,
  /**
   * The date the item was last updated
   */
  lastUpdated: PropTypes.string,
  /**
   * The latest version of the item
   */
  latestVersion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * The name of the item
   */
  name: PropTypes.string,
  /**
   * True if the item is currently selected, false otherwise
   */
  selected: PropTypes.bool,
  /**
   * The item type
   */
  type: PropTypes.string,
  /**
   * "owned" if the item belongs to customer's library, "subscribed" otherwise
   */
  repositoryAccessType: PropTypes.string,
  onSelectRepositoryItem: PropTypes.func,
  onOpenRepositoryItem: PropTypes.func
};

export default RepositoryItemCard;
